import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchWithAuth } from './lib/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/el';
import {
  employeeMatchesPositionFilter,
  areShiftsReadyForWeek,
  getEmployeeWeekPosition,
  shiftsForWeek,
} from './lib/shiftUtils';
import { usePresetHours } from './PresetHoursContext';
import { usePositions } from './PositionsContext';
import EmployeeManagementModal from './components/EmployeeManagementModal';
import { useEmployeePayments, useCumulativePayments, useCreatePayment, useUpdatePayment, useDeletePayment } from './hooks/useEmployeePayments';
import { useShifts } from './hooks/useShifts';
import { useQueryClient } from '@tanstack/react-query';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale('el');

const DAYS = [
  { label: 'Δευ', value: 1 },
  { label: 'Τρί', value: 2 },
  { label: 'Τετ', value: 3 },
  { label: 'Πέμ', value: 4 },
  { label: 'Παρ', value: 5 },
  { label: 'Σάβ', value: 6 },
  { label: 'Κυρ', value: 0 },
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

export default function Schedule3() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { presetHours } = usePresetHours();
  const { positions: positionsList } = usePositions();
  const [selectedWeek, setSelectedWeek] = useState(dayjs().startOf('isoWeek'));
  const [allEmployees, setAllEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const fetchGenerationRef = useRef(0);
  const weekStart = useMemo(() => selectedWeek.format('YYYY-MM-DD'), [selectedWeek]);
  const weekShifts = useMemo(() => shiftsForWeek(shifts, weekStart), [shifts, weekStart]);
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // Search by name
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  
  // Unified Employee Management Modal (same as Staff.js)
  const [unifiedModal, setUnifiedModal] = useState({ open: false, emp: null, tab: 0 });
  const [editForm, setEditForm] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentStats, setPaymentStats] = useState({ totalSalary: 0, totalPaid: 0, unpaid: 0 });
  const [paymentStatsFilter, setPaymentStatsFilter] = useState('current_week');
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyGroupBy, setHistoryGroupBy] = useState('none');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [shiftsEmployeeId, setShiftsEmployeeId] = useState(null);
  
  // React Query hooks (same as Staff.js)
  const queryClient = useQueryClient();
  const weekStartStr = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
  const { data: cumulativeData = [] } = useCumulativePayments(weekStartStr);
  const { data: paymentHistoryData = [], refetch: refetchPaymentHistory } = useEmployeePayments(selectedEmployeeId);
  const { data: employeeShiftsData = [], refetch: refetchShifts } = useShifts(
    shiftsEmployeeId ? { employee_id: shiftsEmployeeId } : {}
  );
  
  // React Query mutations
  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();
  const deletePaymentMutation = useDeletePayment();
  
  // New Payment Dialog
  const [newPaymentDialog, setNewPaymentDialog] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  const [unpaidWeeks, setUnpaidWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  
  // Edit payment state
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({
    payment_date: '',
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  
  // Dialog state - Enhanced modal
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({
    selectedDays: [],
    weekOffset: 0,
    dayTimes: {}
  });

  // Copy Week Dialog state
  const [copyWeekDialog, setCopyWeekDialog] = useState(false);
  const [copyWeekForm, setCopyWeekForm] = useState({
    weeksAhead: 1,
    skipExisting: true
  });
  const [copyingWeek, setCopyingWeek] = useState(false);
  
  // Selected employees for copy (checkboxes in table)
  const [selectedForCopy, setSelectedForCopy] = useState([]);
  
  // Generate fixed schedules state
  const [generatingFixed, setGeneratingFixed] = useState(false);

  const employees = useMemo(() => {
    let filtered = allEmployees;
    const shiftsReady = areShiftsReadyForWeek(shifts, weekStart, loading);

    if (selectedPosition !== '' && shiftsReady) {
      filtered = filtered.filter((emp) =>
        employeeMatchesPositionFilter(emp, shifts, selectedPosition, weekStart)
      );
    }

    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((emp) =>
        (emp.name || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allEmployees, shifts, weekStart, loading, selectedPosition, searchTerm]);

  useEffect(() => {
    setShifts([]);
    fetchGenerationRef.current += 1;
    fetchData(fetchGenerationRef.current);
    fetchPositions();
  }, [selectedWeek]);

  const fetchPositions = async () => {
    try {
      const res = await fetchWithAuth('/api/positions');
      if (res.ok) {
        const data = await res.json();
        setPositions(data.map(p => p.name));
      }
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const refreshData = () => {
    fetchGenerationRef.current += 1;
    fetchData(fetchGenerationRef.current);
  };

  const fetchData = async (fetchGeneration) => {
    setLoading(true);
    setError('');
    const requestedWeekStart = selectedWeek.format('YYYY-MM-DD');

    try {
      const employeesRes = await fetchWithAuth('/api/employees');
      const employeesData = await employeesRes.json();
      const activeEmployees = employeesData.filter((emp) => emp.active !== 0);

      if (fetchGeneration !== fetchGenerationRef.current) return;

      setAllEmployees(activeEmployees);

      const shiftsRes = await fetchWithAuth(`/api/shifts?week_start=${requestedWeekStart}`);
      const shiftsData = await shiftsRes.json();

      if (fetchGeneration !== fetchGenerationRef.current) return;

      setShifts(shiftsData);
    } catch (err) {
      if (fetchGeneration === fetchGenerationRef.current) {
        setError('Σφάλμα φόρτωσης δεδομένων');
        console.error(err);
      }
    } finally {
      if (fetchGeneration === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const getShiftForCell = (employeeId, day) => {
    return weekShifts.find(
      (s) => s.employee_id === employeeId && String(s.day) === String(day)
    );
  };

  const determineShiftType = (startTime) => {
    const [hours] = startTime.split(':').map(Number);
    // Πρωινή βάρδια: 05:00 - 14:59
    // Απογευματινή βάρδια: 15:00 - 04:59
    if (hours >= 5 && hours < 15) {
      return 'morning';
    } else {
      return 'evening';
    }
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleCellClick = (employee, day) => {
    // Get first preset hours as default
    let defaultStart = '09:00';
    let defaultEnd = '17:00';
    
    if (presetHours && presetHours.length > 0) {
      const [start, end] = presetHours[0].split('-');
      defaultStart = start;
      defaultEnd = end;
    }
    
    // Check if editing existing shift
    const existingShift = getShiftForCell(employee.id, day);
    
    // Initialize dayTimes
    const dayTimes = {};
    DAYS_OF_WEEK.forEach(d => {
      if (existingShift && d.value === day) {
        dayTimes[d.value] = {
          startTime: existingShift.start_time,
          endTime: existingShift.end_time
        };
      } else {
        dayTimes[d.value] = {
          startTime: defaultStart,
          endTime: defaultEnd
        };
      }
    });
    
    setSelectedCell({ employee, day, existingShift });
    setQuickAddForm({
      selectedDays: existingShift ? [day] : [],
      weekOffset: 0,
      dayTimes: dayTimes
    });
    setOpenDialog(true);
  };

  const handleDayToggle = (day) => {
    const { selectedDays } = quickAddForm;
    if (selectedDays.includes(day)) {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: selectedDays.filter(d => d !== day)
      });
    } else {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: [...selectedDays, day]
      });
    }
  };

  const getWeekDateRange = (weekOffset) => {
    const monday = dayjs(selectedWeek).add(weekOffset * 7, 'day');
    const sunday = monday.add(6, 'day');
    
    const formatDate = (date) => {
      return date.format('DD/MM/YYYY');
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}`;
  };

  const handleSaveShift = async () => {
    if (!selectedCell) return;

    const { employee } = selectedCell;
    const { selectedDays, dayTimes, weekOffset } = quickAddForm;

    if (selectedDays.length === 0) {
      showAlert('Επιλέξτε τουλάχιστον μία ημέρα', 'warning');
      return;
    }

    // Validate that all selected days have times
    for (const day of selectedDays) {
      const times = dayTimes[day];
      if (!times?.startTime || !times?.endTime) {
        showAlert('Συμπληρώστε ώρες έναρξης και λήξης για όλες τις επιλεγμένες ημέρες', 'warning');
        return;
      }
    }

    try {
      const weekStart = dayjs(selectedWeek).add(weekOffset * 7, 'day').format('YYYY-MM-DD');
      
      const shiftsToCreate = selectedDays.map(dayOfWeek => {
        const times = dayTimes[dayOfWeek];
        const shiftType = determineShiftType(times.startTime);
        
        return {
          day: dayOfWeek,
          employee_id: employee.id,
          start_time: times.startTime,
          end_time: times.endTime,
          week_start: weekStart,
          shift_type: shiftType
        };
      });

      let successCount = 0;
      let duplicateCount = 0;
      let errorCount = 0;

      for (const shift of shiftsToCreate) {
        // Check if editing existing shift
        const existingShift = getShiftForCell(employee.id, shift.day);
        
        try {
          let response;
          if (existingShift && shift.day === selectedCell.day) {
            // Update existing shift
            response = await fetchWithAuth(`/api/shifts/${existingShift.id}`, {
              method: 'PUT',
              body: JSON.stringify(shift)
            });
          } else {
            // Create new shift
            response = await fetchWithAuth('/api/shifts', {
              method: 'POST',
              body: JSON.stringify(shift)
            });
          }

          const data = await response.json();

          if (response.ok) {
            successCount++;
          } else if (response.status === 409 || data.error?.includes('UNIQUE constraint')) {
            duplicateCount++;
          } else {
            console.error('Failed to save shift:', response.status, data);
            errorCount++;
          }
        } catch (err) {
          console.error('Error saving shift:', err);
          errorCount++;
        }
      }

      if (successCount > 0 && duplicateCount === 0 && errorCount === 0) {
        showAlert(`Προστέθηκαν ${successCount} βάρδιες!`, 'success');
        setOpenDialog(false);
        refreshData();
      } else if (successCount > 0) {
        showAlert(`Προστέθηκαν ${successCount} βάρδιες. ${duplicateCount > 0 ? `${duplicateCount} υπήρχαν ήδη.` : ''}`, 'warning');
        setOpenDialog(false);
        refreshData();
      } else if (duplicateCount > 0) {
        showAlert('Όλες οι βάρδιες υπάρχουν ήδη', 'warning');
      } else {
        showAlert('Σφάλμα αποθήκευσης βάρδιας', 'error');
      }
    } catch (error) {
      console.error('Error saving shift:', error);
      showAlert('Σφάλμα αποθήκευσης βάρδιας', 'error');
    }
  };

  const handleDeleteShift = async (shiftId, employeeName) => {
    try {
      // Find the shift to get its details
      const shift = shifts.find(s => s.id === shiftId);
      if (!shift) {
        showAlert('Η βάρδια δεν βρέθηκε', 'error');
        return;
      }

      // Check if there are payments for this shift's week and employee
      const weekStart = shift.week_start;
      const paymentsResponse = await fetchWithAuth(
        `/api/employee-payments/allocations/${shift.employee_id}/${weekStart}`
      );
      
      if (paymentsResponse.ok) {
        const payments = await paymentsResponse.json();
        
        if (payments.length > 0) {
          // Calculate total paid amount
          const totalPaid = payments.reduce((sum, p) => sum + p.allocated_amount, 0);
          
          // Show confirmation dialog
          const confirmMessage = `⚠️ ΠΡΟΣΟΧΗ!\n\n` +
            `Ο/Η ${employeeName} έχει πληρωθεί €${totalPaid.toFixed(2)} για αυτή την εβδομάδα.\n\n` +
            `Αν διαγράψεις αυτή τη βάρδια, θα διαγραφούν ΑΥΤΟΜΑΤΑ και όλες οι πληρωμές της εβδομάδας.\n\n` +
            `Θέλεις να συνεχίσεις;`;
          
          if (!window.confirm(confirmMessage)) {
            return; // User cancelled
          }
          
          // Delete all payments for this week
          for (const payment of payments) {
            await fetchWithAuth(`/api/employee-payments/${payment.payment_id}`, {
              method: 'DELETE'
            });
          }
        }
      }

      // Now delete the shift
      const response = await fetchWithAuth(`/api/shifts/${shiftId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete shift');

      showAlert('Η βάρδια διαγράφηκε επιτυχώς', 'success');
      refreshData(); // Refresh the data
    } catch (error) {
      console.error('Error deleting shift:', error);
      showAlert('Σφάλμα διαγραφής βάρδιας', 'error');
    }
  };

  const formatShiftDisplay = (shift) => {
    if (!shift) return '-';
    return `${shift.start_time}-${shift.end_time}`;
  };

  // ========== GENERATE FIXED SCHEDULES ==========
  const handleGenerateFixedSchedules = async () => {
    setGeneratingFixed(true);
    try {
      const response = await fetchWithAuth('/api/fixed-schedules/generate', {
        method: 'POST',
        body: JSON.stringify({
          week_start: selectedWeek.format('YYYY-MM-DD')
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Σφάλμα δημιουργίας');
      }
      
      if (result.created > 0) {
        showAlert(`Δημιουργήθηκαν ${result.created} βάρδιες από σταθερά προγράμματα`, 'success');
        refreshData(); // Refresh
      } else if (result.skipped > 0) {
        showAlert(`Όλες οι σταθερές βάρδιες υπήρχαν ήδη (${result.skipped} παραλείφθηκαν)`, 'info');
      } else {
        showAlert('Δεν βρέθηκαν υπάλληλοι με σταθερό πρόγραμμα', 'warning');
      }
    } catch (error) {
      console.error('Error generating fixed schedules:', error);
      showAlert(`Σφάλμα: ${error.message}`, 'error');
    } finally {
      setGeneratingFixed(false);
    }
  };

  // ========== COPY WEEK HANDLERS ==========
  const handleOpenCopyWeek = () => {
    setCopyWeekForm({
      weeksAhead: 1,
      selectedEmployees: [],
      skipExisting: true
    });
    setCopyWeekDialog(true);
  };

  const handleCopyWeek = async () => {
    // Get employees with shifts that are selected (or all if none selected)
    const employeesWithShifts = employees.filter((e) => weekShifts.some((s) => s.employee_id === e.id));
    const employeesToCopy = selectedForCopy.length > 0 
      ? selectedForCopy 
      : employeesWithShifts.map(e => e.id);
    
    const shiftsToCount = weekShifts.filter((s) => employeesToCopy.includes(s.employee_id));
    
    if (shiftsToCount.length === 0) {
      showAlert('Δεν υπάρχουν βάρδιες για αντιγραφή', 'warning');
      return;
    }

    setCopyingWeek(true);
    try {
      // Generate target weeks
      const targetWeeks = [];
      for (let i = 1; i <= copyWeekForm.weeksAhead; i++) {
        targetWeeks.push(selectedWeek.add(i, 'week').format('YYYY-MM-DD'));
      }

      const response = await fetchWithAuth('/api/shifts/copy-week', {
        method: 'POST',
        body: JSON.stringify({
          source_week_start: selectedWeek.format('YYYY-MM-DD'),
          target_weeks: targetWeeks,
          employee_ids: selectedForCopy.length > 0 ? selectedForCopy : undefined,
          skip_existing: copyWeekForm.skipExisting
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Σφάλμα αντιγραφής');
      }

      showAlert(result.message, 'success');
      setCopyWeekDialog(false);
      setSelectedForCopy([]); // Clear selection after copy
      
      // Refresh data for current week
      refreshData();
    } catch (error) {
      console.error('Error copying week:', error);
      showAlert(`Σφάλμα: ${error.message}`, 'error');
    } finally {
      setCopyingWeek(false);
    }
  };

  // Calculate payment stats from React Query data (same as Staff.js)
  const paymentStatsCalculated = useMemo(() => {
    if (!selectedEmployeeId || !currentEmployee) {
      return { totalSalary: 0, totalPaid: 0, unpaid: 0 };
    }
    
    let totalSalary = 0;
    if (employeeShiftsData && employeeShiftsData.length > 0) {
      employeeShiftsData.forEach(shift => {
        const startParts = shift.start_time.split(':');
        const endParts = shift.end_time.split(':');
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        if (endMinutes < startMinutes) {
          endMinutes += 24 * 60;
        }
        const hours = (endMinutes - startMinutes) / 60;
        
        if (hours > 0) {
          // USE STORED WAGE FROM SHIFT FOR HISTORICAL ACCURACY
          // Never use current employee data for existing shifts
          const wageRate = shift.hourly_wage || 0;
          totalSalary += hours * wageRate;
        }
      });
    }
    
    const totalPaid = paymentHistoryData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    const unpaid = totalSalary - totalPaid;
    
    return { totalSalary: totalSalary || 0, totalPaid: totalPaid || 0, unpaid: unpaid || 0 };
  }, [selectedEmployeeId, currentEmployee, employeeShiftsData, paymentHistoryData]);

  // Update paymentStats state when calculated stats change
  useEffect(() => {
    setPaymentStats(paymentStatsCalculated);
  }, [paymentStatsCalculated]);

  // Update local state when data changes
  useEffect(() => {
    if (paymentHistoryData) {
      setPaymentHistory(Array.isArray(paymentHistoryData) ? paymentHistoryData : []);
    }
  }, [paymentHistoryData]);

  useEffect(() => {
    if (employeeShiftsData) {
      setEmployeeShifts(Array.isArray(employeeShiftsData) ? employeeShiftsData : []);
    }
  }, [employeeShiftsData]);

  // Refetch shifts when unified modal is open on payment history tab
  useEffect(() => {
    if (unifiedModal.open && unifiedModal.tab === 2 && unifiedModal.emp) {
      refetchShifts();
    }
  }, [unifiedModal.open, unifiedModal.tab, unifiedModal.emp, refetchShifts]);

  // Employee Management Modal Functions (same as Staff.js)
  const openUnifiedModal = async (emp, tab = 0) => {
    setCurrentEmployee(emp);
    setUnifiedModal({ open: true, emp, tab });
    
    setEditForm({
      name: emp.name,
      position: emp.position,
      hourly_wage: emp.hourly_wage,
      saturday_wage: emp.saturday_wage || '',
      sunday_wage: emp.sunday_wage || '',
      shift_preference: emp.shift_preference,
      max_days_per_week: emp.max_days_per_week || 7,
      available_days: emp.available_days || '0,1,2,3,4,5,6',
      shift_preferences: emp.shift_preferences || 'any',
      notes: emp.notes || ''
    });
    
    setSelectedEmployeeId(emp.id);
    setShiftsEmployeeId(emp.id);
    
    setTimeout(() => {
      refetchShifts();
    }, 100);
  };

  const handleOpenNewPayment = async () => {
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
    setSelectedWeeks([]);
    
    const currentEmp = unifiedModal.emp;
    
    if (currentEmp && cumulativeData) {
      try {
        const employeeData = cumulativeData.find(e => e.id === currentEmp.id);
        if (employeeData && employeeData.unpaid_weeks) {
          const sortedUnpaid = employeeData.unpaid_weeks.sort((a, b) => 
            new Date(a.week_start) - new Date(b.week_start)
          );
          setUnpaidWeeks(sortedUnpaid);
        } else {
          setUnpaidWeeks([]);
        }
      } catch (error) {
        console.error('Error processing unpaid weeks:', error);
        setUnpaidWeeks([]);
      }
    } else {
      setUnpaidWeeks([]);
    }
    
    setNewPaymentDialog(true);
  };

  const handleCloseNewPayment = () => {
    setNewPaymentDialog(false);
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleSaveNewPayment = async () => {
    if (!newPaymentForm.amount || parseFloat(newPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    const currentEmp = unifiedModal.emp;
    if (!currentEmp) {
      showAlert('Σφάλμα: Δεν βρέθηκε ο υπάλληλος', 'error');
      return;
    }

    try {
      let remainingAmount = parseFloat(newPaymentForm.amount);
      const allocations = [];
      
      for (const week of unpaidWeeks) {
        if (remainingAmount <= 0) break;
        const weekUnpaid = week.unpaid;
        const allocationAmount = Math.min(remainingAmount, weekUnpaid);
        allocations.push({
          week_start: week.week_start,
          allocated_amount: allocationAmount
        });
        remainingAmount -= allocationAmount;
      }
      
      if (remainingAmount > 0 || allocations.length === 0) {
        const currentWeekStart = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
        if (allocations.length === 0) {
          allocations.push({
            week_start: currentWeekStart,
            allocated_amount: parseFloat(newPaymentForm.amount)
          });
        } else {
          allocations.push({
            week_start: currentWeekStart,
            allocated_amount: remainingAmount
          });
        }
      }

      let paymentDate = newPaymentForm.payment_date;
      if (paymentDate && paymentDate.includes('T')) {
        paymentDate = paymentDate.split('T')[0];
      }
      
      const paymentData = {
        employee_id: currentEmp.id,
        payment_date: paymentDate,
        amount: parseFloat(newPaymentForm.amount),
        payment_method: newPaymentForm.payment_method || 'cash',
        reference: newPaymentForm.reference || null,
        notes: newPaymentForm.notes || null,
        allocations: allocations
      };
      
      const result = await createPaymentMutation.mutateAsync(paymentData);
      showAlert('Η πληρωμή καταχωρήθηκε επιτυχώς', 'success');
      handleCloseNewPayment();
      if (currentEmp) {
        refetchPaymentHistory();
      }
    } catch (error) {
      console.error('Error saving payment:', error);
      showAlert(`Σφάλμα κατά την αποθήκευση της πληρωμής: ${error.message}`, 'error');
    }
  };

  // Calculate filtered stats based on date range (same as Staff.js)
  const calculateFilteredStats = () => {
    if (!historyDateFrom && !historyDateTo) {
      return null;
    }
    
    let filteredShifts = [...employeeShifts];
    filteredShifts = filteredShifts.map(shift => {
      const day = parseInt(shift.day);
      const actualDate = dayjs(shift.week_start).add(day === 0 ? 6 : day - 1, 'day');
      return { ...shift, actualDate };
    });
    
    if (historyDateFrom) {
      filteredShifts = filteredShifts.filter(s => s.actualDate.isSameOrAfter(dayjs(historyDateFrom), 'day'));
    }
    if (historyDateTo) {
      filteredShifts = filteredShifts.filter(s => s.actualDate.isSameOrBefore(dayjs(historyDateTo), 'day'));
    }
    
    let totalSalary = 0;
    filteredShifts.forEach(shift => {
      const startParts = shift.start_time.split(':');
      const endParts = shift.end_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }
      const hours = (endMinutes - startMinutes) / 60;
      
      if (hours > 0 && currentEmployee) {
        // USE STORED WAGE FROM SHIFT FOR HISTORICAL ACCURACY
        // Never use current employee data for existing shifts
        const wageRate = shift.hourly_wage || 0;
        totalSalary += hours * wageRate;
      }
    });
    
    let filteredPayments = [...paymentHistory];
    if (historyDateFrom) {
      filteredPayments = filteredPayments.filter(p => dayjs(p.payment_date).isSameOrAfter(dayjs(historyDateFrom), 'day'));
    }
    if (historyDateTo) {
      filteredPayments = filteredPayments.filter(p => dayjs(p.payment_date).isSameOrBefore(dayjs(historyDateTo), 'day'));
    }
    
    const totalPaid = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
    const unpaid = totalSalary - totalPaid;
    
    return { totalSalary, totalPaid, unpaid };
  };

  const getFilteredAndGroupedHistory = () => {
    let filtered = [...paymentHistory];
    
    if (historyDateFrom) {
      filtered = filtered.filter(p => dayjs(p.payment_date).isSameOrAfter(dayjs(historyDateFrom), 'day'));
    }
    if (historyDateTo) {
      filtered = filtered.filter(p => dayjs(p.payment_date).isSameOrBefore(dayjs(historyDateTo), 'day'));
    }
    
    if (historyGroupBy === 'none') {
      return filtered.map(p => ({
        ...p,
        groupLabel: null,
        items: [p]
      }));
    }
    
    const groups = {};
    filtered.forEach(payment => {
      let groupKey;
      let groupLabel;
      
      if (historyGroupBy === 'day') {
        groupKey = dayjs(payment.payment_date).format('YYYY-MM-DD');
        groupLabel = dayjs(payment.payment_date).format('DD/MM/YYYY');
      } else if (historyGroupBy === 'week') {
        const weekStart = dayjs(payment.payment_date).startOf('isoWeek').format('YYYY-MM-DD');
        const weekEnd = dayjs(payment.payment_date).endOf('isoWeek').format('YYYY-MM-DD');
        groupKey = weekStart;
        groupLabel = `Εβδομάδα ${dayjs(weekStart).format('DD/MM/YYYY')} - ${dayjs(weekEnd).format('DD/MM/YYYY')}`;
      } else if (historyGroupBy === 'month') {
        groupKey = dayjs(payment.payment_date).format('YYYY-MM');
        groupLabel = dayjs(payment.payment_date).format('MMMM YYYY');
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          groupKey,
          groupLabel,
          items: [],
          totalAmount: 0
        };
      }
      
      groups[groupKey].items.push(payment);
      groups[groupKey].totalAmount += parseFloat(payment.amount || 0);
    });
    
    return Object.values(groups).sort((a, b) => b.groupKey.localeCompare(a.groupKey));
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    const emp = unifiedModal.emp;
    if (!emp) return;

    try {
      const response = await fetchWithAuth(`/api/employees/${emp.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name,
          position: editForm.position,
          hourly_wage: parseFloat(editForm.hourly_wage),
          saturday_wage: editForm.saturday_wage ? parseFloat(editForm.saturday_wage) : null,
          sunday_wage: editForm.sunday_wage ? parseFloat(editForm.sunday_wage) : null,
          shift_preference: editForm.shift_preference
        })
      });

      if (response.ok) {
        showAlert('Οι αλλαγές αποθηκεύτηκαν επιτυχώς', 'success');
        setUnifiedModal({ open: false, emp: null, tab: 0 });
        refreshData();
      } else {
        showAlert('Σφάλμα αποθήκευσης', 'error');
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      showAlert('Σφάλμα αποθήκευσης', 'error');
    }
  };

  const handlePreferencesSave = async () => {
    const emp = unifiedModal.emp;
    if (!emp) return;

    try {
      const response = await fetchWithAuth(`/api/employees/${emp.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          max_days_per_week: parseInt(editForm.max_days_per_week),
          available_days: editForm.available_days,
          shift_preferences: editForm.shift_preferences,
          notes: editForm.notes
        })
      });

      if (response.ok) {
        showAlert('Οι προτιμήσεις αποθηκεύτηκαν επιτυχώς', 'success');
        setUnifiedModal({ open: false, emp: null, tab: 0 });
        refreshData();
      } else {
        showAlert('Σφάλμα αποθήκευσης', 'error');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      showAlert('Σφάλμα αποθήκευσης', 'error');
    }
  };

  const handleOpenEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentForm({
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_method: payment.payment_method,
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
    setEditPaymentDialog(true);
  };

  const handleCloseEditPayment = () => {
    setEditPaymentDialog(false);
    setEditingPayment(null);
    setEditPaymentForm({
      payment_date: '',
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleSaveEditPayment = async () => {
    if (!editPaymentForm.amount || parseFloat(editPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    if (!editingPayment) {
      showAlert('Δεν υπάρχει πληρωμή προς επεξεργασία', 'error');
      return;
    }

    try {
      const result = await updatePaymentMutation.mutateAsync({
        id: editingPayment.id,
        data: {
          ...editPaymentForm,
          amount: parseFloat(editPaymentForm.amount),
          employee_id: editingPayment.employee_id
        }
      });
      showAlert('Η πληρωμή ενημερώθηκε επιτυχώς!', 'success');
      handleCloseEditPayment();
      refetchPaymentHistory();
    } catch (error) {
      console.error('Error updating payment:', error);
      showAlert(`Σφάλμα ενημέρωσης πληρωμής: ${error.message}`, 'error');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) {
      return;
    }

    try {
      await deletePaymentMutation.mutateAsync(paymentId);
      showAlert('Η πληρωμή διαγράφηκε επιτυχώς!', 'success');
      refetchPaymentHistory();
    } catch (error) {
      console.error('Error deleting payment:', error);
      showAlert(`Σφάλμα διαγραφής πληρωμής: ${error.message}`, 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
      <Box sx={{ p: { xs: 0, md: 3 } }}>
        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          gutterBottom 
          sx={{ fontWeight: 600, mb: { xs: 2, md: 3 } }}
        >
          Πρόγραμμα 3
        </Typography>

        {/* Week Picker and Position Filter */}
        <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' }, 
            gap: { xs: 1.5, md: 2 }, 
            flexWrap: 'wrap' 
          }}>
            {/* Date picker row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexWrap: 'wrap'
            }}>
              <DatePicker
                label={isMobile ? "Εβδομάδα" : "Επιλογή Εβδομάδας"}
                value={selectedWeek}
                onChange={(newValue) => setSelectedWeek(dayjs(newValue).startOf('isoWeek'))}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { 
                      minWidth: { xs: 140, md: 'auto' },
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                      }
                    }
                  } 
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#616161',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                {selectedWeek.format('DD/MM')} - {selectedWeek.add(6, 'day').format('DD/MM/YYYY')}
              </Typography>
              
              {/* Copy Week Button */}
              <Button
                variant={selectedForCopy.length > 0 ? "contained" : "outlined"}
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleOpenCopyWeek}
                disabled={weekShifts.length === 0}
                sx={{ 
                  ml: 1,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.7rem', md: '0.8rem' }
                }}
              >
                {selectedForCopy.length > 0 
                  ? `Αντιγραφή (${selectedForCopy.length})` 
                  : (isMobile ? 'Αντιγραφή' : 'Αντιγραφή Εβδομάδας')}
              </Button>
              
              {/* Generate Fixed Schedules Button */}
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={generatingFixed ? null : <AutorenewIcon />}
                onClick={handleGenerateFixedSchedules}
                disabled={generatingFixed}
                sx={{ 
                  ml: 1,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.7rem', md: '0.8rem' }
                }}
                title="Δημιουργία βαρδιών από σταθερά προγράμματα υπαλλήλων"
              >
                {generatingFixed ? 'Δημιουργία...' : (isMobile ? 'Σταθερά' : 'Σταθερά Προγράμματα')}
              </Button>
            </Box>
            
            {!isMobile && <Box sx={{ flexGrow: 1 }} />}
            
            {/* Filters row */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <TextField
                size="small"
                label={isMobile ? "Αναζήτηση" : "Αναζήτηση ονόματος"}
                placeholder="Όνομα..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: { xs: 'auto', md: 200 } }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: 'auto', md: 180 } }}>
                <InputLabel>{isMobile ? "Πόστο" : "Φίλτρο Πόστων"}</InputLabel>
                <Select
                  value={selectedPosition}
                  label={isMobile ? "Πόστο" : "Φίλτρο Πόστων"}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Όλα</em>
                  </MenuItem>
                  {positions.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Schedule Grid */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2,
            overflowX: 'auto',
            // Add scroll hint shadow on mobile
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: 3,
            },
          }}
        >
          <Table sx={{ minWidth: isMobile ? 600 : 750 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f1f2f3' }}>
                {/* Checkbox column for copy selection */}
                <TableCell 
                  sx={{ 
                    width: 40,
                    padding: { xs: '4px', md: '8px' },
                    backgroundColor: '#f1f2f3',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedForCopy.length > 0 && 
                      selectedForCopy.length === employees.filter((e) => weekShifts.some((s) => s.employee_id === e.id)).length}
                    indeterminate={selectedForCopy.length > 0 && 
                      selectedForCopy.length < employees.filter((e) => weekShifts.some((s) => s.employee_id === e.id)).length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Select all employees with shifts
                        setSelectedForCopy(employees.filter((emp) => weekShifts.some((s) => s.employee_id === emp.id)).map((emp) => emp.id));
                      } else {
                        setSelectedForCopy([]);
                      }
                    }}
                    title="Επιλογή όλων"
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 600, 
                    minWidth: { xs: 80, md: 150 },
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    padding: { xs: '8px 6px', md: '16px' },
                    position: isMobile ? 'sticky' : 'static',
                    left: 0,
                    backgroundColor: '#f1f2f3',
                    zIndex: 1,
                  }}
                >
                  {isMobile ? 'Όνομα' : 'Υπάλληλος'}
                </TableCell>
                {DAYS.map((day) => (
                  <TableCell 
                    key={day.value} 
                    align="center" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      padding: { xs: '8px 4px', md: '16px' },
                      minWidth: { xs: 55, md: 80 },
                    }}
                  >
                    {day.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => {
                const hasShifts = weekShifts.some((s) => s.employee_id === employee.id);
                const isSelected = selectedForCopy.includes(employee.id);
                
                return (
                  <TableRow 
                    key={employee.id} 
                    hover
                    sx={{ 
                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                    }}
                  >
                    {/* Checkbox for copy selection */}
                    <TableCell 
                      sx={{ 
                        width: 40,
                        padding: { xs: '4px', md: '8px' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={!hasShifts}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForCopy([...selectedForCopy, employee.id]);
                          } else {
                            setSelectedForCopy(selectedForCopy.filter(id => id !== employee.id));
                          }
                        }}
                        title={hasShifts ? 'Επιλογή για αντιγραφή' : 'Δεν έχει βάρδιες'}
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        padding: { xs: '8px 6px', md: '16px' },
                        position: isMobile ? 'sticky' : 'static',
                        left: 0,
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : '#fff',
                        zIndex: 1,
                        maxWidth: { xs: 80, md: 'none' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box
                        onClick={() => openUnifiedModal(employee, 0)}
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark',
                          },
                        }}
                      >
                        {isMobile ? employee.name.split(' ')[0] : employee.name}
                        {!isMobile && weekShifts.some((s) => s.employee_id === employee.id) && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {getEmployeeWeekPosition(employee, shifts, weekStart)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  {DAYS.map((day) => {
                    const shift = getShiftForCell(employee.id, day.value);
                    const hasShift = !!shift;
                    
                    return (
                      <TableCell
                        key={day.value}
                        align="center"
                        sx={{
                          backgroundColor: hasShift ? '#e8f5e9' : 'transparent',
                          transition: 'background-color 0.2s',
                          fontFamily: 'monospace',
                          fontSize: { xs: '0.65rem', md: '0.875rem' },
                          padding: { xs: '6px 2px', md: '8px 4px' },
                          minWidth: { xs: 55, md: 80 },
                        }}
                      >
                        {hasShift ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: { xs: 0, md: 0.5 } 
                          }}>
                            <Box
                              onClick={() => handleCellClick(employee, day.value)}
                              sx={{
                                cursor: 'pointer',
                                fontSize: { xs: '0.65rem', md: '0.875rem' },
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {formatShiftDisplay(shift)}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteShift(shift.id, employee.name);
                              }}
                              sx={{
                                padding: { xs: '1px', md: '2px' },
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: { xs: 12, md: 16 } }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => handleCellClick(employee, day.value)}
                            sx={{
                              cursor: 'pointer',
                              padding: { xs: '6px', md: '8px' },
                              minHeight: { xs: 28, md: 36 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&:active': {
                                backgroundColor: '#e0e0e0',
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: { xs: 14, md: 18 }, color: '#bbb' }} />
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Enhanced Add/Edit Shift Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Γρήγορη Προσθήκη στο Πρόγραμμα
            {selectedCell && (
              <Typography variant="body2" color="textSecondary">
                {selectedCell.employee.name} - Μπουφές
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {/* Week Selection */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Επιλέξτε Εβδομάδα:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <IconButton 
                    onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset - 1 })}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="body1" sx={{ minWidth: '200px', textAlign: 'center', fontWeight: 'medium' }}>
                    {getWeekDateRange(quickAddForm.weekOffset)}
                  </Typography>
                  <IconButton 
                    onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset + 1 })}
                    size="small"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Days Selection with Individual Times */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Επιλέξτε Ημέρες και Ωράριο:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="ΩΩ:ΛΛ"
                      sx={{ width: '80px' }}
                      inputProps={{ style: { fontSize: '0.875rem' } }}
                      id="bulk-start-time"
                    />
                    <Typography variant="body2">—</Typography>
                    <TextField
                      size="small"
                      placeholder="ΩΩ:ΛΛ"
                      sx={{ width: '80px' }}
                      inputProps={{ style: { fontSize: '0.875rem' } }}
                      id="bulk-end-time"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const bulkStart = document.getElementById('bulk-start-time').value;
                        const bulkEnd = document.getElementById('bulk-end-time').value;
                        if (bulkStart && bulkEnd) {
                          const newDayTimes = { ...quickAddForm.dayTimes };
                          DAYS_OF_WEEK.forEach(day => {
                            newDayTimes[day.value] = {
                              startTime: bulkStart,
                              endTime: bulkEnd
                            };
                          });
                          setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                        }
                      }}
                    >
                      Εφαρμογή σε Όλες
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {DAYS_OF_WEEK.map((day) => (
                    <Box 
                      key={day.value}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        p: 1.5,
                        border: quickAddForm.selectedDays.includes(day.value) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: quickAddForm.selectedDays.includes(day.value) ? '#f0f7ff' : 'transparent'
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={quickAddForm.selectedDays.includes(day.value)}
                            onChange={() => handleDayToggle(day.value)}
                          />
                        }
                        label={<Typography sx={{ minWidth: '80px', fontWeight: 'medium' }}>{day.label}</Typography>}
                        sx={{ m: 0 }}
                      />
                      <TextField
                        label="Έναρξη"
                        type="text"
                        size="small"
                        value={quickAddForm.dayTimes[day.value]?.startTime || ''}
                        onChange={(e) => {
                          const newDayTimes = { ...quickAddForm.dayTimes };
                          newDayTimes[day.value] = {
                            ...newDayTimes[day.value],
                            startTime: e.target.value
                          };
                          setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                        }}
                        placeholder="09:00"
                        sx={{ width: '120px' }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                      <TextField
                        label="Λήξη"
                        type="text"
                        size="small"
                        value={quickAddForm.dayTimes[day.value]?.endTime || ''}
                        onChange={(e) => {
                          const newDayTimes = { ...quickAddForm.dayTimes };
                          newDayTimes[day.value] = {
                            ...newDayTimes[day.value],
                            endTime: e.target.value
                          };
                          setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                        }}
                        placeholder="17:00"
                        sx={{ width: '120px' }}
                        InputLabelProps={{ shrink: true }}
                      />
                      {presetHours && presetHours.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {presetHours.map((preset, idx) => {
                            const [startTime, endTime] = preset.split('-');
                            return (
                              <Chip
                                key={idx}
                                label={preset}
                                size="small"
                                onClick={() => {
                                  const newDayTimes = { ...quickAddForm.dayTimes };
                                  newDayTimes[day.value] = {
                                    startTime: startTime,
                                    endTime: endTime
                                  };
                                  setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                                }}
                                sx={{ 
                                  cursor: 'pointer',
                                  fontSize: '0.7rem',
                                  height: '22px'
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Άκυρο</Button>
            <Button onClick={handleSaveShift} variant="contained" color="success">
              Προσθήκη στο Πρόγραμμα
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unified Employee Management Modal */}
        <EmployeeManagementModal
          open={unifiedModal.open}
          employee={unifiedModal.emp}
          tab={unifiedModal.tab}
          onClose={() => {
            setUnifiedModal({ open: false, emp: null, tab: 0 });
          }}
          onTabChange={(newTab) => {
            setUnifiedModal({ ...unifiedModal, tab: newTab });
          }}
          positions={positionsList}
          editForm={editForm}
          onEditFormChange={handleEditChange}
          onEditFormSet={setEditForm}
          onEditSave={handleEditSave}
          onPreferencesSave={handlePreferencesSave}
          paymentStats={paymentStats}
          paymentHistory={paymentHistory}
          employeeShifts={employeeShiftsData || employeeShifts}
          onOpenNewPayment={handleOpenNewPayment}
          onOpenEditPayment={handleOpenEditPayment}
          onDeletePayment={handleDeletePayment}
          showFixedSchedule={false}
          onRefetchPaymentHistory={refetchPaymentHistory}
          onRefetchShifts={refetchShifts}
        />

        {/* Edit Payment Dialog */}
        <Dialog open={editPaymentDialog} onClose={handleCloseEditPayment} maxWidth="sm" fullWidth>
          <DialogTitle>
            Επεξεργασία Πληρωμής
            {editingPayment && (
              <Typography variant="body2" color="textSecondary">
                Πληρωμή #{editingPayment.id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Ημερομηνία Πληρωμής"
                  InputLabelProps={{ shrink: true }}
                  value={editPaymentForm.payment_date}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ποσό"
                  value={editPaymentForm.amount}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                  InputProps={{ startAdornment: '€' }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Τρόπος Πληρωμής</InputLabel>
                  <Select
                    value={editPaymentForm.payment_method}
                    label="Τρόπος Πληρωμής"
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_method: e.target.value })}
                  >
                    <MenuItem value="cash">Μετρητά</MenuItem>
                    <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                    <MenuItem value="check">Επιταγή</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Αριθμός Αναφοράς"
                  value={editPaymentForm.reference}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, reference: e.target.value })}
                  placeholder="π.χ. αριθμός επιταγής"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Σημειώσεις"
                  multiline
                  rows={3}
                  value={editPaymentForm.notes}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditPayment}>Ακύρωση</Button>
            <Button onClick={handleSaveEditPayment} variant="contained" color="primary">
              Αποθήκευση
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Payment Dialog */}
        <Dialog open={newPaymentDialog} onClose={handleCloseNewPayment} maxWidth="md" fullWidth>
          <DialogTitle>
            Νέα Πληρωμή
            {unifiedModal.emp && (
              <Typography variant="body2" color="textSecondary">
                {unifiedModal.emp.name} - {getEmployeeWeekPosition(unifiedModal.emp, shifts, weekStart)}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Ημερομηνία Πληρωμής"
                  InputLabelProps={{ shrink: true }}
                  value={newPaymentForm.payment_date}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Ποσό"
                  value={newPaymentForm.amount}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, amount: e.target.value })}
                  InputProps={{ startAdornment: '€' }}
                />
              </Grid>
              
              {/* Unpaid Weeks Info */}
              {unpaidWeeks.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Η πληρωμή θα κατανεμηθεί αυτόματα στις παλαιότερες απλήρωτες εβδομάδες (FIFO)
                    </Typography>
                  </Alert>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Απλήρωτες Εβδομάδες
                    <Chip 
                      label={`Σύνολο: €${unpaidWeeks.reduce((sum, w) => sum + w.unpaid, 0).toFixed(2)}`} 
                      size="small" 
                      color="error" 
                    />
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: '#f9f9f9' }}>
                    {unpaidWeeks.map((week, index) => {
                      const paymentAmount = parseFloat(newPaymentForm.amount) || 0;
                      const previousWeeksTotal = unpaidWeeks.slice(0, index).reduce((sum, w) => sum + w.unpaid, 0);
                      const remainingForThisWeek = Math.max(0, paymentAmount - previousWeeksTotal);
                      const willBeAllocated = Math.min(remainingForThisWeek, week.unpaid);
                      
                      return (
                        <Box 
                          key={week.week_start} 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            p: 1, 
                            mb: 0.5,
                            bgcolor: willBeAllocated > 0 ? '#e8f5e9' : 'transparent',
                            borderRadius: 1,
                            border: willBeAllocated > 0 ? '1px solid #4caf50' : '1px solid transparent'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: willBeAllocated > 0 ? 'bold' : 'normal' }}>
                              {index + 1}. {dayjs(week.week_start).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Χρέος: €{week.unpaid.toFixed(2)}
                            </Typography>
                          </Box>
                          {willBeAllocated > 0 && (
                            <Chip 
                              label={`Θα πληρωθεί: €${willBeAllocated.toFixed(2)}`}
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Τρόπος Πληρωμής</InputLabel>
                  <Select
                    value={newPaymentForm.payment_method}
                    label="Τρόπος Πληρωμής"
                    onChange={(e) => setNewPaymentForm({ ...newPaymentForm, payment_method: e.target.value })}
                  >
                    <MenuItem value="cash">Μετρητά</MenuItem>
                    <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                    <MenuItem value="check">Επιταγή</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Αριθμός Αναφοράς"
                  value={newPaymentForm.reference}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, reference: e.target.value })}
                  placeholder="π.χ. αριθμός επιταγής"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Σημειώσεις"
                  multiline
                  rows={3}
                  value={newPaymentForm.notes}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNewPayment}>Ακύρωση</Button>
            <Button onClick={handleSaveNewPayment} variant="contained" color="primary">
              Αποθήκευση
            </Button>
          </DialogActions>
        </Dialog>

        {/* Copy Week Dialog */}
        <Dialog open={copyWeekDialog} onClose={() => setCopyWeekDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Αντιγραφή Εβδομάδας
            <Typography variant="body2" color="textSecondary">
              Αντιγραφή βαρδιών από: {selectedWeek.format('DD/MM/YYYY')} - {selectedWeek.add(6, 'day').format('DD/MM/YYYY')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {/* Current week info */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  {selectedForCopy.length === 0 ? (
                    <>
                      <strong>{weekShifts.length}</strong> βάρδιες θα αντιγραφούν από <strong>{employees.filter((e) => weekShifts.some((s) => s.employee_id === e.id)).length}</strong> υπαλλήλους (όλοι)
                    </>
                  ) : (
                    <>
                      <strong>{weekShifts.filter((s) => selectedForCopy.includes(s.employee_id)).length}</strong> βάρδιες θα αντιγραφούν από <strong>{selectedForCopy.length}</strong> επιλεγμένους υπαλλήλους
                    </>
                  )}
                </Typography>
              </Alert>
              
              {/* Selected employees preview */}
              {selectedForCopy.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Επιλεγμένοι υπάλληλοι:
                  </Typography>
                  {employees
                    .filter(emp => selectedForCopy.includes(emp.id))
                    .map(emp => (
                      <Chip
                        key={emp.id}
                        label={emp.name}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                        onDelete={() => setSelectedForCopy(selectedForCopy.filter(id => id !== emp.id))}
                      />
                    ))}
                </Box>
              )}

              {/* Weeks ahead selection */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Αντιγραφή σε πόσες εβδομάδες μπροστά;</InputLabel>
                <Select
                  value={copyWeekForm.weeksAhead}
                  label="Αντιγραφή σε πόσες εβδομάδες μπροστά;"
                  onChange={(e) => setCopyWeekForm({ ...copyWeekForm, weeksAhead: e.target.value })}
                >
                  <MenuItem value={1}>1 εβδομάδα ({selectedWeek.add(1, 'week').format('DD/MM')} - {selectedWeek.add(1, 'week').add(6, 'day').format('DD/MM')})</MenuItem>
                  <MenuItem value={2}>2 εβδομάδες</MenuItem>
                  <MenuItem value={3}>3 εβδομάδες</MenuItem>
                  <MenuItem value={4}>4 εβδομάδες (1 μήνα)</MenuItem>
                  <MenuItem value={8}>8 εβδομάδες (2 μήνες)</MenuItem>
                </Select>
              </FormControl>

              {/* Target weeks preview */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Θα δημιουργηθούν βάρδιες για:
                </Typography>
                {Array.from({ length: copyWeekForm.weeksAhead }, (_, i) => {
                  const targetWeek = selectedWeek.add(i + 1, 'week');
                  return (
                    <Chip
                      key={i}
                      label={`${targetWeek.format('DD/MM')} - ${targetWeek.add(6, 'day').format('DD/MM')}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>

              {/* Skip existing option */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={copyWeekForm.skipExisting}
                    onChange={(e) => setCopyWeekForm({ ...copyWeekForm, skipExisting: e.target.checked })}
                  />
                }
                label="Παράλειψη αν υπάρχει ήδη βάρδια (προτεινόμενο)"
              />
              
              {!copyWeekForm.skipExisting && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Οι υπάρχουσες βάρδιες θα αντικατασταθούν!
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCopyWeekDialog(false)} disabled={copyingWeek}>
              Ακύρωση
            </Button>
            <Button 
              onClick={handleCopyWeek} 
              variant="contained" 
              color="primary"
              disabled={copyingWeek}
              startIcon={copyingWeek ? null : <ContentCopyIcon />}
            >
              {copyingWeek ? 'Αντιγραφή...' : `Αντιγραφή σε ${copyWeekForm.weeksAhead} εβδομάδα(ες)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alert */}
        {alert && (
          <Alert severity={alert.severity} sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
            {alert.message}
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
}
